import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(10px);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const ChatContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
`;

export const ChatToggleButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #1689ca;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: rgba(15, 15, 15, 0.06) 0px 1px 6px, rgba(15, 15, 15, 0.16) 0px 2px 32px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #1478b3;
    transform: scale(1.05);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: white;
  }
`;

export const ChatWindow = styled.div<{ isOpen: boolean; isClosing: boolean }>`
  position: absolute;
  bottom: 60px;
  right: 0;
  width: 380px;
  height: 550px;
  max-height: calc(100vh - 100px);
  background: white;
  border-radius: 16px;
  box-shadow: rgba(15, 15, 15, 0.16) 0px 5px 40px 0px;
  display: ${props => (props.isOpen || props.isClosing) ? 'flex' : 'none'};
  flex-direction: column;
  animation: ${props => props.isClosing ? fadeOut : fadeIn} 0.3s ease forwards;
  overflow: hidden;
`;

export const ChatHeader = styled.div`
  background: #1689ca;
  color: white;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ChatTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
`;

export const ChatSubtitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  opacity: 0.8;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  svg {
    width: 16px;
    height: 16px;
    fill: #fff;
  }
`;

export const ChatBody = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fff;
`;

export const Message = styled.div<{ isUser: boolean }>`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

export const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 90%;
  padding: 12px 16px;
  border-radius: 16px;
  background: ${props => props.isUser ? '#e0f7fa' : '#f0f0f0'};
  color: ${props => props.isUser ? '#000' : '#333'};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  
  /* Markdown styling */
  p {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    font-weight: 600;
  }
  
  em {
    font-style: italic;
  }
  
  ul, ol {
    margin: 8px 0;
    padding-left: 20px;
  }
  
  li {
    margin: 4px 0;
  }
  
  code {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 4px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
  }
  
  pre {
    background: rgba(0, 0, 0, 0.1);
    padding: 8px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
    
    code {
      background: none;
      padding: 0;
    }
  }
  
  blockquote {
    border-left: 3px solid #ccc;
    margin: 8px 0;
    padding-left: 12px;
    color: #666;
  }
  
  /* Table styling */
  .table-wrapper {
    overflow-x: auto;
    margin: 8px 0;
    border-radius: 6px;
    border: 1px solid #ddd;
  }
  
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0;
    font-size: 13px;
    min-width: 100%;
  }
  
  th, td {
    border: 1px solid #ddd;
    padding: 8px 12px;
    text-align: left;
  }
  
  th {
    background-color: rgba(0, 0, 0, 0.05);
    font-weight: 600;
  }
  
  tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  tr:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  /* Heading styles - smaller sizes for chat */
  h1 {
    font-size: 1.6rem;
    font-weight: 600;
    margin: 0 0 8px 0;
    line-height: 1.2;
  }
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 6px 0;
    line-height: 1.2;
  }
  
  h3 {
    font-size: 1.4rem;
    font-weight: 600;
    margin: 0 0 4px 0;
    line-height: 1.2;
  }
  
  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0 0 4px 0;
    line-height: 1.2;
  }
  
  h5 {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 2px 0;
    line-height: 1.2;
  }
  
  h6 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 2px 0;
    line-height: 1.2;
  }
`;

export const LoadingMessage = styled.div`
  display: flex;
  justify-content: flex-start;
`;

export const LoadingBubble = styled.div`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 16px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e0e0e0;
  border-top: 2px solid #1689ca;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const LoadingText = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #666;
`;

export const ChatInput = styled.div`
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 8px;
`;

export const InputField = styled.input`
  flex: 1;
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  outline: none;
  
  &:focus {
    border-color: #1689ca;
  }
  
  &::placeholder {
    color: #999;
  }
`;

export const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #1689ca;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #1478b3;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
    fill: white;
  }
`;
